<template>
    <div
        v-click-outside="hideDropdown"
        class="paginator"
        :class="{ paginator_light: lightStyle, paginator_light_blue: blueStyle, [`mb-${marginBottom}`]: blueStyle }"
        :style="{ marginLeft: `${marginLeft}px` }"
    >
        <div class="paginator-per-page" v-if="withPerPage">
            <div @click="isDropdownOpen = true" class="paginator-per-page__current">
                {{ t("expert.show") }} <span class="ml-4">{{ perPage }}</span> <ArrowDownIcon />
            </div>
            <transition name="fade-up">
                <div class="paginator-per-page__dropdown" v-show="isDropdownOpen">
                    <component
                        :is="preventDefault ? 'a' : 'router-link'"
                        @click="handlePerPage($event, perPageCase)"
                        @click.native="handlePerPage($event, perPageCase)"
                        class="paginator-per-page__item"
                        :class="{
                            'paginator-per-page__item_active':
                                perPageCase === perPage ||
                                (hasRouter() && perPageCase.toString() === $route.query.per_page)
                        }"
                        :to="
                            hasRouter()
                                ? { path: $route.path, query: { ...$route.query, per_page: perPageCase, page: 1 } }
                                : undefined
                        "
                        :key="index"
                        v-for="(perPageCase, index) in perPageCases"
                    >
                        {{ t("expert.show") }} {{ perPageCase }}
                    </component>
                </div>
            </transition>
        </div>
        <div class="paginator-links" v-if="totalPages > 1 || blueStyle">
            <template v-if="!linked && totalPages > 1">
                <button
                    @click="isPreviousPageActionDisabled ? undefined : $emit('change:page', currentPage - 1)"
                    :class="{ paginator__action_disabled: isPreviousPageActionDisabled }"
                    class="paginator__action paginator__action_previous"
                >
                    <ArrowDownSmallIcon />
                    <ArrowDownSmallIcon v-if="lightStyle" />
                </button>
                <div class="paginator__links">
                    <button
                        @click="$emit('change:page', page)"
                        v-show="approveLinkVisibility(page)"
                        :class="getLinkClasses(page, 'paginator__links__item')"
                        :key="index"
                        class="paginator__links__item"
                        v-for="(page, index) in totalPages"
                    >
                        {{ page }}
                    </button>
                </div>
                <button
                    @click="isNextPageActionDisabled ? undefined : $emit('change:page', currentPage + 1)"
                    :class="{ paginator__action_disabled: isNextPageActionDisabled }"
                    class="paginator__action paginator__action_next"
                >
                    <ArrowDownSmallIcon />
                    <ArrowDownSmallIcon v-if="lightStyle" />
                </button>
            </template>
            <template v-else-if="!blueStyle">
                <component
                    :is="preventDefault ? 'a' : 'router-link'"
                    @click="handlePage($event, currentPage - 1)"
                    @click.native="handlePage($event, currentPage - 1)"
                    :disabled="isPreviousPageActionDisabled"
                    :to="{ path: $route.path, query: { ...$route.query, page: currentPage - 1 } }"
                    :class="{ paginator__action_disabled: isPreviousPageActionDisabled }"
                    class="paginator__action paginator__action_previous"
                >
                    <ArrowDownSmallIcon />
                    <ArrowDownSmallIcon v-if="lightStyle" />
                </component>
                <div class="paginator__links">
                    <component
                        :is="preventDefault ? 'a' : 'router-link'"
                        @click="handlePage($event, page)"
                        @click.native="handlePage($event, page)"
                        :to="{ path: $route.path, query: { ...$route.query, page: page } }"
                        v-show="approveLinkVisibility(page)"
                        :class="getLinkClasses(page, 'paginator__links__item')"
                        :key="index"
                        class="paginator__links__item"
                        v-for="(page, index) in totalPages"
                    >
                        {{ page }}
                    </component>
                </div>
                <component
                    :is="preventDefault ? 'a' : 'router-link'"
                    @click="handlePage($event, currentPage + 1)"
                    @click.native="handlePage($event, currentPage + 1)"
                    :disabled="isPreviousPageActionDisabled"
                    :to="{ path: $route.path, query: { ...$route.query, page: currentPage + 1 } }"
                    :class="{ paginator__action_disabled: isNextPageActionDisabled }"
                    class="paginator__action paginator__action_next"
                >
                    <ArrowDownSmallIcon />
                    <ArrowDownSmallIcon v-if="lightStyle" />
                </component>
            </template>
            <template v-else>
                <component
                    :is="preventDefault ? 'a' : 'router-link'"
                    @click="handlePage($event, 1)"
                    @click.native="handlePage($event, 1)"
                    :disabled="isSuperPreviousPageActionDisabled"
                    :to="hasRouter() ? { path: $route.path, query: { ...$route.query, page: 1 } } : undefined"
                    :class="{ paginator__action_disabled: isPreviousPageActionDisabled }"
                    class="paginator__action paginator__action_previous"
                >
                    <i v-html="icons.doubleArrow"></i>
                </component>
                <component
                    :is="preventDefault ? 'a' : 'router-link'"
                    @click="handlePage($event, currentPage - 1)"
                    @click.native="handlePage($event, currentPage - 1)"
                    :disabled="isPreviousPageActionDisabled"
                    :to="
                        hasRouter()
                            ? { path: $route.path, query: { ...$route.query, page: currentPage - 1 } }
                            : undefined
                    "
                    :class="{ paginator__action_disabled: isPreviousPageActionDisabled }"
                    class="paginator__action paginator__action_previous"
                >
                    <i v-html="icons.arrow"></i>
                </component>
                <div class="paginator__links">
                    <div class="paginator__links__item">
                        <span
                            >{{ perPage * (currentPage - 1) + 1 }} -
                            {{
                                totalItems >= 0 ? Math.min(perPage * currentPage, totalItems) : perPage * currentPage
                            }}</span
                        >
                        <template v-if="totalItems >= 0">
                            {{ t("course.from") }}
                            {{ totalItems }}
                        </template>
                    </div>
                </div>
                <component
                    :is="preventDefault ? 'a' : 'router-link'"
                    @click="handlePage($event, currentPage + 1)"
                    @click.native="handlePage($event, currentPage + 1)"
                    :disabled="isNextPageActionDisabled"
                    :to="
                        hasRouter()
                            ? { path: $route.path, query: { ...$route.query, page: currentPage + 1 } }
                            : undefined
                    "
                    :class="{ paginator__action_disabled: isNextPageActionDisabled }"
                    class="paginator__action paginator__action_next"
                >
                    <i v-html="icons.arrow"></i>
                </component>
                <component
                    v-if="totalPages >= 0"
                    :is="preventDefault ? 'a' : 'router-link'"
                    @click="handlePage($event, totalPages)"
                    @click.native="handlePage($event, totalPages)"
                    :disabled="isSuperNextPageActionDisabled"
                    :to="
                        hasRouter()
                            ? { path: $route && $route.path, query: { ...$route.query, page: totalPages } }
                            : undefined
                    "
                    :class="{ paginator__action_disabled: isSuperNextPageActionDisabled }"
                    class="paginator__action paginator__action_next"
                >
                    <i v-html="icons.doubleArrow"></i>
                </component>
            </template>
        </div>
    </div>
</template>

<script>
import ArrowDownIcon from "@icons/ArrowDownIcon"
import ClickOutside from "vue-click-outside"
import ArrowDownSmallIcon from "@icons/ArrowDownSmallIcon.vue"

const icons = {
    doubleArrow: `<svg width="34" height="34" viewBox="0 0 34 34" fill="none" stroke="#3E4755" xmlns="http://www.w3.org/2000/svg">
<path d="M18.416 24.0832L25.4993 16.9998L18.416 9.9165"  stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M9.91602 24.0832L16.9993 16.9998L9.91602 9.9165" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`,
    arrow: `<svg width="34" height="34" viewBox="0 0 34 34" fill="none" stroke="#3E4755" xmlns="http://www.w3.org/2000/svg">
<path d="M14.166 24.0832L21.2493 16.9998L14.166 9.9165"  stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`
}

export default {
    components: {
        ArrowDownSmallIcon,
        ArrowDownIcon
    },
    directives: {
        ClickOutside
    },
    props: {
        currentPage: {
            required: false,
            default: 1,
            type: [Number, String]
        },
        marginBottom: {
            required: false,
            default: 100,
            type: Number
        },
        totalPages: {
            required: false,
            type: Number
        },
        totalItems: {
            required: false,
            type: Number,
            default: 100
        },
        lightStyle: {
            required: false,
            type: Boolean
        },
        blueStyle: {
            required: false,
            type: Boolean
        },
        linked: {
            required: false,
            type: Boolean
        },
        marginLeft: {
            required: false,
            type: Number
        },
        withPerPage: {
            required: false,
            type: Boolean,
            default: true
        },
        perPage: {
            required: false,
            type: [Number, String]
        },
        perPageCases: {
            required: false,
            type: Array
        },
        preventDefault: {
            //w/o params in routing = true
            required: false,
            default: false,
            type: Boolean
        },
        scrollTable: {
            required: false,
            default: true,
            type: Boolean
        }
    },
    name: "CustomPaginator",
    data() {
        return {
            isDropdownOpen: false,
            icons
        }
    },
    methods: {
        hasRouter() {
            return this.$route
        },
        handlePagination() {
            if (
                this.$parent &&
                this.$parent.$el &&
                this.$parent.$el.querySelector(".default-table") &&
                this.scrollTable
            ) {
                this.$parent.$el.querySelector(".default-table").scrollIntoView()
            }
        },
        handlePage(event, page) {
            if (this.preventDefault) {
                event.preventDefault()
            }

            this.handlePagination()
            this.$emit("update:page", page)
            this.$emit("update")
        },
        handlePerPage(event, page) {
            if (this.preventDefault) {
                event.preventDefault()
            }

            this.$emit("update:page", 1)
            this.$emit("update:perPage", page)
            this.$emit("update")
            this.hideDropdown()
        },
        approveLinkVisibility(page) {
            if (page === 1 || page === this.totalPages) {
                return true
            } else if (page >= this.currentPage && page <= this.currentPage + (this.currentPage === 1 ? 2 : 1)) {
                return true
            } else if (page >= this.totalPages - 2 && this.currentPage >= this.totalPages - 2) {
                return true
            }
            return false
        },

        getLinkClasses(page, prefix = "paginator__links__item") {
            let modifications = null

            if (page !== 1 && page !== this.totalPages) {
                modifications = {
                    active: this.currentPage === page
                }
            } else {
                modifications = {
                    // Added condition "this.totalPages > 4" to show "..." only if total pages q-ty is 5 and more
                    active: this.currentPage === page,
                    eclipsed: page === 1 || page === this.totalPages,
                    eclipsed_start: page === 1 && this.totalPages > 4,
                    eclipsed_end: page === this.totalPages && this.totalPages > 4,
                    eclipsed_start_visible: this.currentPage > 2 && page === 1 && this.totalPages > 4,
                    eclipsed_end_visible: this.currentPage <= 2 && page === this.totalPages && this.totalPages > 4
                }
            }

            Object.keys(modifications).map(key => {
                modifications[`${prefix}_${key}`] = modifications[key]
                delete modifications[key]
            })

            return modifications
        },
        hideDropdown() {
            this.isDropdownOpen = false
        }
    },
    computed: {
        isNextPageActionDisabled() {
            return this.currentPage === this.totalPages
        },
        isPreviousPageActionDisabled() {
            return this.currentPage === 1
        },
        isSuperPreviousPageActionDisabled() {
            return this.currentPage < this.perPage
        },
        isSuperNextPageActionDisabled() {
            return this.currentPage === this.totalPages
        }
    },
    watch: {
        $route() {
            this.hideDropdown()
        }
    }
}
</script>
<style lang="sass">
@import "resources/sass/vars"
.paginator-per-page
    position: relative
    width: 150px
    @media screen and (max-width: 320px)
        width: auto
    &__current
        cursor: pointer
        display: flex
        align-items: center
        font-size: 13px
        font-weight: 400
        min-height: 24px
        color: #151515
        line-height: 17px
        svg
            margin-left: 6px
    &__dropdown
        padding: 10px
        box-shadow: 0 10px 40px rgba(128, 158, 191, 0.2)
        border-radius: 10px
        width: 160px
        position: absolute
        top: 0
        background: #fff
        z-index: 555
    &__item
        font-size: 13px
        font-weight: 400
        color: #151515
        line-height: 17px
        height: 39px
        border-radius: 5px
        cursor: pointer
        display: flex
        align-items: center
        padding-left: 14px
        transition: .2s

        &_active
            background: #F2F7FF
            cursor: default
        &:not(&:last-child)
            margin-bottom: 2px
        &:hover
            background: #F2F7FF
.paginator
    width: 100%
    display: flex
    justify-content: center
    align-items: center
    padding: 12px 0
    &.dropdown-bottom
        .paginator-per-page__dropdown
            bottom: 0
            top: auto
    &-links
        display: flex
        justify-content: center
        align-items: center
    &__action
        cursor: pointer
        width: 42px
        height: 42px
        border-radius: 10px
        background: rgba(214, 227, 246, 1)
        display: flex
        justify-content: center
        align-items: center
        transition: .3s
        &_previous
            svg
                fill: black
                transform: rotate(90deg)
        &_next
            svg
                fill: black
                transform: rotate(-90deg)
        &_disabled
            background: rgba(214, 227, 246, 0.5)
            cursor: default
            svg
                fill: #cfcfcf
    &__links
        margin: 0 24px
        display: flex
        align-items: center
        &__item
            width: 26px
            height: 26px
            border-radius: 50%
            margin: 0 10px
            font-size: 16px
            font-weight: 600
            transition: .4s
            cursor: pointer
            &:not(&_active):hover
                background-color: #eee
            &_active
                background-color: #FF7439
                color: white
            &_eclipsed
                position: relative
                &_start
                    &:before
                        content: "..."
                        display: none
                    &_visible
                        margin-right: 36px
                        &:before
                            display: block
                            position: absolute
                            right: -28px
                &_end
                    &:before
                        content: "..."
                        display: none
                    &_visible
                        margin-left: 44px
                        &:before
                            display: block
                            position: absolute
                            left: -32px

    &_light
        width: max-content
        .paginator__action
            width: 24px
            min-width: 24px
            height: 24px
            background-color: transparent
            i
                width: 9px
            &_previous
                i:first-child
                    transform: translateX(3px)
                svg
                    fill: #52565C
                    transform: rotate(90deg)
            &_next
                i:last-child
                    transform: translateX(-3px)
                svg
                    fill: #52565C
                    transform: rotate(-90deg)
            &_disabled
                cursor: default
                pointer-events: none
                svg
                    fill: #DDDDDD
        .paginator__links
            margin: 0 7px
            @media screen and (max-width: 320px)
                margin: 0 3px
            &__item
                width: 15px
                height: max-content
                border-radius: initial
                margin: 0 7px
                font-size: 13px
                font-weight: 600
                line-height: 17px
                transition: 0s
                color: #52565C
                font-family: $mainFont
                @media screen and (max-width: 320px)
                    margin: 0 3px
                &:not(&_active):hover
                    background-color: transparent
                &_active
                    color: #FF7439
                    background-color: transparent
                &_eclipsed
                    position: relative
                    &_start
                        &:before
                            content: "..."
                            color: #52565C
                            display: none
                        &_visible
                            margin-right: 32px
                            &:before
                                display: block
                                position: absolute
                                right: -24px
                    &_end
                        &:before
                            content: "..."
                            color: #52565C
                            display: none
                        &_visible
                            margin-left: 32px
                            &:before
                                display: block
                                position: absolute
                                left: -24px
        &_blue
            padding: 0px
            min-height: 54px
            text-transform: lowercase
            @media (max-width: 380px)
                flex-direction: column-reverse
                align-items: flex-end
                .paginator-per-page
                    margin-top: 10px
            .paginator-per-page__dropdown
                bottom: 0
                top: auto
            .paginator-links
                padding: 10px
            .paginator__links
                margin: 0 10px
                @media (max-width: 960px)
                    margin: 0 5px
                &__item
                    font-size: 16px
                    width: max-content
                    display: flex
                    align-items: center
                    white-space: nowrap
                    font-weight: 400
                    @media (max-width: 960px)
                        font-size: 13px
                    span
                        margin-right: 5px
                        color: #3965FF
            .paginator__action
                width: 34px
                height: 34px
                min-width: 34px
                @media (max-width: 960px)
                    width: 22px
                    height: 22px
                    min-width: 22px
                &_previous
                    svg
                        fill: none
                        transform: rotate(180deg)
                        stroke: #3E4755
                &_next
                    svg
                        fill: none
                        transform: none
                        stroke: #3E4755
                &_disabled
                    svg
                        stroke: #DDDDDD !important
                &_previous, &_next
                    &:hover
                       svg
                           stroke: #3965FF
                i
                    transform: none !important
                    width: 34px
                    @media (max-width: 960px)
                        width: 22px
                        height: 22px
                        min-width: 22px
                        svg
                            width: 22px
                            height: 22px
                            min-width: 22px
            .paginator-per-page
                width: auto
                &__current
                    margin-right: 10px
                    font-size: 16px
                    padding: 10px
                    font-weight: 400
                    @media (max-width: 960px)
                        font-size: 13px
                        margin-right: 0
                    span
                        color: #3965FF
                    svg
                        width: 14px
</style>
